import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Button, CircularProgress, IconButton, Popover, Tooltip } from "@mui/material";
import { RefreshData } from "components/helper";
import DeleteModal from "components/Modals/DeleteModal";
import WholeScreenLoader from "components/WholeScreenLoader";
import { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import { FaDownload, FaPlusCircle } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { truncateText } from "components/helper";
import axios from "axios";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import PreivewModal from "./PreivewModal";
import { hasPermission } from "utils/permissionUtils";
import { P } from "const/permissions";

const RPLCourse = ({ color = "light" }) => {
  const navigate = useNavigate();
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [{ rplCourseList, token, isRPLCourseListFetching }, { refetchRPLCourseList }] = useKothar();

  useEffect(() => {
    if (!rplCourseList?.length) {
      refetchRPLCourseList();
    }
  }, [rplCourseList?.length]);

  const handleDeleteData = () => {
    deleteData(
      `${API_URL}/rpl-course/${openConfirmationModal?.id}`,
      () => {
        setOpenConfirmationModal({ state: false, id: null });
        refetchRPLCourseList();
      },
      token
    );
  };
  const [loading, setLoading] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const previewCSVData = async () => {
    const res = await axios.get(`${API_URL}/rpl-course/preview`);
    return res?.data?.data?.reverse();
  };
  const {
    data: previewData,
    refetch: refetchPreviewData,
    isFetching: isPreviewFetching,
  } = useQuery(["previewData"], previewCSVData, {
    refetchOnWindowFocus: false,
    enabled: false,
    onSuccess: (data) => {
      setPreviewModalOpen(true);
    },
  });

  const postSelectedRPLCourses = async (data) => {
    const res = await axios.post(`${API_URL}/import-selected`, data);
    return res?.data?.data;
  };

  const { mutate: postSelectedRPLCoursesMutation } = useMutation(postSelectedRPLCourses, {
    onSuccess: (data) => {
      toast.success("Data successfully imported");
      refetchRPLCourseList();
      setPreviewModalOpen(false);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const fetchSelectedRPLCourses = (data) => {
    postSelectedRPLCoursesMutation({ selectedCourses: data });
  };

  const importSelectedRPLCourseData = () => {
    if (window.confirm("Are you sure you want to import selected RPL course data?")) {
      refetchPreviewData();
    }
  };

  const fetchCSVData = () => {
    setLoading(true);

    const apiUrl = `${API_URL}/rpl-course/import`;
    axios
      .get(apiUrl) // Use axios to fetch the CSV data
      .then((response) => {
        refetchRPLCourseList();
        setLoading(false);
        toast.success("Data successfully fetched from Google Sheets");
      })
      .catch((error) => {
        console.error("Error fetching CSV data:", error);
      });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "courseName",
        header: "Course Name",
        size: 150,
      },
      {
        accessorKey: "courseCode",
        header: "Course Code",
        size: 150,
        Cell: ({ renderedCellValue }) => {
          return (
            <Tooltip title={renderedCellValue} arrow placement="top">
              <div className="truncate">{renderedCellValue ?? "-"}</div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "collegeName",
        header: "College Name",
        size: 120,
      },
      {
        accessorKey: "rtoCode",
        header: "RTO Code",
        size: 150,
      },
      {
        accessorKey: "minCost",
        header: "Minimum Cost",
        size: 150,
      },
      {
        accessorKey: "maxCost",
        header: "Maximum Cost",
        size: 150,
      },
      {
        accessorKey: "sellCost",
        header: "Selling Cost",
        size: 150,
      },
      {
        accessorKey: "requirements",
        header: "Requirements",
        size: 150,
        maxSize: 150,
        Cell: ({ renderedCellValue }) => {
          return (
            <Tooltip title={renderedCellValue} arrow placement="top">
              <div className="truncate">{truncateText(renderedCellValue, 40)}</div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "note",
        header: "Note",
        size: 150,
        Cell: ({ renderedCellValue }) => {
          return (
            <Tooltip title={renderedCellValue} arrow placement="top">
              <div className="truncate">{truncateText(renderedCellValue, 40)}</div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row, renderedCellValue }) => {
          const item = row.original;
          const [anchorEl, setAnchorEl] = useState(null);

          const handleClick = (event) => {
            console.log(row, event, "clicked");
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          const open = Boolean(anchorEl);

          return (
            <div className="flex items-center gap-2">
              <IconButton onClick={handleClick}>
                <MoreVertIcon className="text-app-text-secondary" />
              </IconButton>
              <Popover
                id={open ? "action-popover" : undefined}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                {hasPermission(P.RPL_COURSE.CREATE) && (
                  <div className="p-2 min-w-[200px]">
                    <div
                      className="flex items-center gap-2 p-2 hover:bg-app-surface-hover rounded cursor-pointer"
                      onClick={() => {
                        if (hasPermission(P.RPL_COURSE.CREATE)) {
                          navigate("/admin/rpl-course/add", {
                            state: { item },
                          });
                        } else {
                          toast.error("You are not authorized to edit RPL Course");
                        }
                        handleClose();
                      }}
                    >
                      <AiFillEdit className="text-primary" />
                      <span>Edit RPL Course</span>
                    </div>

                    <div
                      className="flex items-center gap-2 p-2 hover:bg-app-surface-hover rounded cursor-pointer text-red-600"
                      onClick={() => {
                        if (hasPermission(P.RPL_COURSE.CREATE)) {
                          setOpenConfirmationModal({
                            state: true,
                            id: item?.id,
                          });
                        } else {
                          toast.error("You are not authorized to delete RPL Course");
                        }
                        handleClose();
                      }}
                    >
                      <AiFillDelete className="text-red-600" />
                      <span>Delete RPL Course</span>
                    </div>
                  </div>
                )}
              </Popover>
            </div>
          );
        },
      },
    ],
    [navigate, setOpenConfirmationModal]
  );

  const table = useMaterialReactTable({
    columns,
    data: rplCourseList || [],
    rowNumberDisplayMode: "original",
    enableRowNumbers: true,
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
    },
    state: {
      isLoading: isRPLCourseListFetching,
      showProgressBars: isRPLCourseListFetching,
      isSaving: isRPLCourseListFetching,
    },
    enablePagination: false,
    enableStickyHeader: true,
    enableColumnPinning: true,
    muiSkeletonProps: {
      animation: "wave",
    },
    muiLinearProgressProps: {
      color: "secondary",
    },
    muiCircularProgressProps: {
      color: "secondary",
    },

    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh",
        minHeight: "70vh", // Set max height
        overflowY: "auto", // Enable vertical scrolling
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        // Check if the click was on an action button or its child
        const isActionClick =
          event.target.closest(".MuiIconButton-root") || event.target.closest(".MuiPopover-root");
        if (!isActionClick) {
          navigate(`/admin/rpl-course/view/${row.original?.id}`, {
            state: { item: row.original },
          });
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className={"font-semibold text-lg " + "text-app-text"}>
                  RPL Courses ({rplCourseList?.length})
                </h3>

                <RefreshData refreshData={refetchRPLCourseList} />
              </div>
              {hasPermission(P.RPL_COURSE.CREATE) && (
                <div className="flex items-center gap-4">
                  <Button
                    variant="contained"
                    startIcon={
                      !isPreviewFetching ? (
                        <FaDownload />
                      ) : (
                        <CircularProgress size={15} sx={{ color: "white", fontSize: "13px" }} />
                      )
                    }
                    onClick={importSelectedRPLCourseData}
                  >
                    Import Selected RPL Course Data
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      // !loading ? (
                      <FaDownload />
                      // ) : (
                      //   <CircularProgress
                      //     sx={{ color: "white", fontSize: "13px" }}
                      //   />
                      // )
                    }
                    onClick={fetchCSVData}
                  >
                    Import RPL Course Data
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<FaPlusCircle />}
                    component={Link}
                    to="/admin/rpl-course/add"
                  >
                    Add RPL Course
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="block w-full overflow-x-auto">
            {loading && <WholeScreenLoader open={loading} />}
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="RPL Course"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={() => handleDeleteData()}
        />
      )}
      {previewModalOpen && (
        <PreivewModal
          open={previewModalOpen}
          data={previewData}
          handleClose={() => setPreviewModalOpen(false)}
          isFetching={isPreviewFetching}
          fetchSelectedRPLCourses={fetchSelectedRPLCourses}
        />
      )}
    </div>
  );
};

export default RPLCourse;
